import * as turf from '@turf/turf';
import { APP } from '../state.js';
import { isochroneRingColors } from '../data/constants.js';
import { toast } from '../ui/toast.js';
import { isochrone as apiIsochrone } from '../api/routing.js';
import { auth } from '../auth/state.js';
import { setLegend, resetLegend } from '../ui/legend.js';

// The UserLayerManager instance is registered by main.js so we can poll
// the user's currently-loaded layers and tally features inside isochrone
// polygons.
let userLayersRef = null;
export function registerUserLayers(manager) { userLayersRef = manager; }

export function activateIsochrone(map) {
  APP.activeTool = 'isochrone';
  map.getCanvas().style.cursor = 'crosshair';
  document.querySelectorAll('.mt-btn').forEach(b => b.classList.remove('active'));
  toast('Click anywhere — or any point/line — to compute travel time');

  // Catch the FIRST click anywhere on the map. Layer-specific click handlers
  // (in userLayers.js) intercept first when a feature is hit, call
  // runIsochrone, and clear APP.activeTool — so this once-handler becomes a
  // no-op if a feature was hit. Otherwise (empty map), runs from here.
  map.once('click', e => {
    if (APP.activeTool === 'isochrone') runIsochrone(map, e.lngLat);
  });
}

export function runIsochroneAt(map, lng, lat) {
  return runIsochrone(map, { lng, lat });
}

export async function runIsochrone(map, lngLat) {
  APP.activeTool = null;
  map.getCanvas().style.cursor = '';

  if (!auth.isAuthenticated()) {
    toast('Sign in to use real isochrones');
    return;
  }

  const intervals = document.getElementById('iso-intervals').value.split(',').map(Number);
  const mode      = document.getElementById('iso-mode').value;

  toast('Computing real travel-time polygons…');
  document.getElementById('iso-results').innerHTML =
    '<div style="font-size:11.5px;color:var(--text3);padding:10px 0">Routing…</div>';

  let resp;
  try {
    resp = await apiIsochrone({ lat: lngLat.lat, lng: lngLat.lng, mode, intervals_min: intervals });
  } catch (e) {
    document.getElementById('iso-results').innerHTML = '';
    toast(`Routing failed: ${e.message}`);
    return;
  }

  // Sort the returned features by `contour` (minutes) ascending so the
  // smallest polygon paints on top of the larger ones, then attach our
  // editorial color per ring.
  const features = (resp.features || [])
    .slice()
    .sort((a, b) => (a.properties?.contour ?? 0) - (b.properties?.contour ?? 0));

  features.forEach((f, i) => {
    f.properties = {
      ...f.properties,
      color:   isochroneRingColors[i] ?? isochroneRingColors[isochroneRingColors.length - 1],
      minutes: f.properties?.contour,
    };
  });

  // Reverse so smallest paints last (= on top)
  map.getSource('iso').setData({ type: 'FeatureCollection', features: [...features].reverse() });
  map.flyTo({ center: [lngLat.lng, lngLat.lat], zoom: 10, duration: 1200 });

  // Update legend with the actual travel-time rings rendered on the map.
  setLegend(`Travel time · ${mode}`, features.map((f, i) => ({
    color: f.properties.color,
    label: `${f.properties.minutes} min`,
    dashed: true,   // matches the dashed line styling of the polygons
  })));

  // Catchment breakdown — count features from each visible vector layer
  // that fall inside each ring.
  const userLayers = userLayersRef?.getVisibleVectorLayersWithFeatures?.() ?? [];

  const breakdown = features.map(f => {
    const polygon = f;
    const minutes = f.properties.minutes;
    const perLayer = userLayers.map(l => ({
      name:  l.name,
      count: countIntersecting(l.features, polygon),
    }));
    return { minutes, perLayer };
  });

  const fmt = (n) => n.toLocaleString();

  document.getElementById('iso-results').innerHTML = `
    <div class="iso-results">
      <h5>Travel-time catchment · ${mode}</h5>
      ${breakdown.map((r, i) => {
        const ring = isochroneRingColors[i] || isochroneRingColors[isochroneRingColors.length - 1];
        const headStat = `<div class="iso-stat"><span class="is-l" style="color:${ring}">● ${r.minutes} min</span><span class="is-v"></span></div>`;
        const rows = r.perLayer.map(l =>
          `<div class="iso-stat"><span class="is-l" style="padding-left:14px">${escape(l.name)}</span><span class="is-v">${fmt(l.count)}</span></div>`
        ).join('');
        return headStat + rows;
      }).join('')}
    </div>
    <button class="btn-sm btn-outline" style="width:100%;margin-top:8px" data-action="clearIsochrone">Clear isochrone</button>
  `;
  toast(`Isochrone — ${intervals.join('/')}-min ${mode}`);
}

/**
 * How many features in `features` intersect the polygon? Uses cheap point-
 * in-polygon for points, full intersect test for everything else (lines,
 * polygons). Also pre-filters by bbox for a real speed-up on large layers.
 */
function countIntersecting(features, polygon) {
  if (!features?.length) return 0;
  const bbox = turf.bbox(polygon);   // [minX, minY, maxX, maxY]
  let n = 0;
  for (const f of features) {
    if (!f.geometry) continue;

    // Cheap bbox prefilter on the feature's first coordinate (works for
    // points; for lines/polygons we just skip the prefilter).
    if (f.geometry.type === 'Point') {
      const [x, y] = f.geometry.coordinates;
      if (x < bbox[0] || x > bbox[2] || y < bbox[1] || y > bbox[3]) continue;
      if (turf.booleanPointInPolygon(f, polygon)) n++;
    } else {
      try {
        if (turf.booleanIntersects(f, polygon)) n++;
      } catch {
        // Skip malformed geometries silently — never block the rest of the count.
      }
    }
  }
  return n;
}

function escape(s) {
  return String(s ?? '').replace(/[&<>"]/g, c => ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;' }[c]));
}

export function clearIsochrone(map) {
  map.getSource('iso').setData({ type: 'FeatureCollection', features: [] });
  document.getElementById('iso-results').innerHTML = '';
  resetLegend();
}
